import { useState } from "react";
import Papa from "papaparse";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CSV_FIELDS } from "@/lib/prospects";

const autoMap = (header) => {
  const h = header.toLowerCase().trim();
  const found = CSV_FIELDS.find((f) => f.id === h || f.label.toLowerCase().replace(" *", "") === h);
  if (found) return found.id;
  if (h.includes("raison") || h === "entreprise" || h === "societe" || h === "société") return "raison_sociale";
  if (h.includes("mail") && h.includes("contact")) return "contact_email";
  if (h.includes("mail")) return "email_generique";
  if (h.includes("tel") || h.includes("tél")) return "telephone";
  if (h.includes("prenom") || h.includes("prénom")) return "contact_prenom";
  if (h.includes("fonction")) return "contact_fonction";
  if (h === "nom") return "contact_nom";
  if (h.includes("ville")) return "ville";
  if (h.includes("postal") || h === "cp") return "code_postal";
  if (h.includes("depart") || h.includes("départ")) return "departement";
  if (h.includes("secteur") || h.includes("activit")) return "secteur";
  if (h.includes("site") || h.includes("web")) return "site_internet";
  if (h.includes("siret")) return "siret";
  if (h.includes("siren")) return "siren";
  if (h.includes("adresse")) return "adresse";
  if (h.includes("besoin")) return "besoin_potentiel";
  return "ignorer";
};

export const ImportCsvDialog = ({ open, onClose, onImported }) => {
  const [step, setStep] = useState(0);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep(0); setHeaders([]); setRows([]); setMapping({}); setReport(null); };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: ({ data, meta }) => {
        if (!data.length) { toast.error("Fichier vide ou illisible"); return; }
        setHeaders(meta.fields || []);
        setRows(data);
        setMapping(Object.fromEntries((meta.fields || []).map((h) => [h, autoMap(h)])));
        setStep(1);
      },
      error: () => toast.error("Impossible de lire le fichier CSV"),
    });
  };

  const mappedRows = () =>
    rows.map((r) => {
      const out = {};
      for (const [col, field] of Object.entries(mapping)) {
        if (field !== "ignorer" && r[col] != null && String(r[col]).trim() !== "") out[field] = String(r[col]).trim();
      }
      return out;
    });

  const analyze = async () => {
    if (!Object.values(mapping).includes("raison_sociale")) {
      toast.error("Vous devez associer une colonne à « Raison sociale »");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/prospects/import/analyze", { rows: mappedRows() });
      setReport(data);
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/prospects/import/confirm", { rows: mappedRows() });
      toast.success(`${data.importes} prospect(s) importé(s), ${data.rejetes} rejeté(s)`);
      onImported?.();
      reset();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="import-csv-dialog">
        <DialogHeader><DialogTitle className="font-display">Importer des prospects (CSV) — étape {step + 1}/3</DialogTitle></DialogHeader>

        {step === 0 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Sélectionnez un fichier CSV avec en-têtes de colonnes. Vous pourrez ensuite faire correspondre les colonnes.</p>
            <input type="file" accept=".csv,text/csv" onChange={onFile} className="block w-full rounded-md border p-3 text-sm" data-testid="csv-file-input" />
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{rows.length} ligne(s) détectée(s). Associez chaque colonne du fichier à un champ.</p>
            <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
              {headers.map((h) => (
                <div key={h} className="flex items-center gap-3">
                  <span className="w-1/2 truncate text-sm font-medium">{h}</span>
                  <Select value={mapping[h]} onValueChange={(v) => setMapping({ ...mapping, [h]: v })}>
                    <SelectTrigger className="w-1/2" data-testid={`mapping-${h}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ignorer">— Ignorer —</SelectItem>
                      {CSV_FIELDS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <Button onClick={analyze} disabled={busy} className="w-full" data-testid="csv-analyze-button">
              {busy ? "Analyse…" : "Analyser avant importation"}
            </Button>
          </div>
        )}

        {step === 2 && report && (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="rounded-md bg-secondary p-2"><p className="font-display text-xl font-semibold">{report.total}</p><p className="text-xs text-muted-foreground">Total</p></div>
              <div className="rounded-md bg-green-100 p-2"><p className="font-display text-xl font-semibold text-green-800">{report.valide}</p><p className="text-xs text-green-800">Valides</p></div>
              <div className="rounded-md bg-amber-100 p-2"><p className="font-display text-xl font-semibold text-amber-800">{report.avertissement}</p><p className="text-xs text-amber-800">Avertissements</p></div>
              <div className="rounded-md bg-red-100 p-2"><p className="font-display text-xl font-semibold text-red-800">{report.rejete}</p><p className="text-xs text-red-800">Rejetées</p></div>
            </div>
            <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1" data-testid="import-report-lines">
              {report.lignes.map((l) => (
                <div key={l.ligne} className="flex flex-wrap items-center gap-2 rounded-md border p-2 text-sm">
                  <Badge variant={l.statut === "valide" ? "default" : l.statut === "avertissement" ? "secondary" : "destructive"}>
                    L{l.ligne} — {l.statut === "valide" ? "OK" : l.statut === "avertissement" ? "Avertissement" : "Rejetée"}
                  </Badge>
                  <span className="font-medium">{l.data.raison_sociale || "(sans nom)"}</span>
                  {l.raisons.length > 0 && <span className="text-xs text-muted-foreground">{l.raisons.join(" · ")}</span>}
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">Aucune ligne n'est supprimée silencieusement : les lignes rejetées ne seront pas importées, la raison est affichée ci-dessus.</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">Modifier la correspondance</Button>
              <Button onClick={confirm} disabled={busy || report.valide + report.avertissement === 0} className="flex-1" data-testid="csv-confirm-button">
                {busy ? "Importation…" : `Importer ${report.valide + report.avertissement} ligne(s)`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
